import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Print,
  Share,
  Person,
  Bed,
  LocalHospital,
  CalendarToday,
  Description,
  AttachMoney,
  MedicalServices,
  Assignment,
  Timeline,
  Phone,
  Email,
  LocationOn,
  AccessTime,
  CheckCircle,
  Warning,
  Info,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getIPDAdmission,
  dischargePatient,
  transferPatient,
  getAvailableBeds,
  listIPDTransfers,
  updateAdmissionDetails,
  listAdmissionAudits,
  listWardRounds,
  listLabOrders,
  listPatientXrays,
  listUltrasounds,
} from "../../../../api/ipd";
import MedicalEquipmentManager from "./MedicalEquipmentManager";
import NurseAssignments from "./NurseAssignments";
import PrescriptionsList from "../../../../Components/PrescriptionsList";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import useFetchObjects from "../../../../api/useFetchObjects";
import TicketPrint from "../../Ticketing/TicketPrint";
import axios from "axios";

const AdmissionView = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const { id } = useParams();
  const apiBase = process.env.REACT_APP_API_URL || "";
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsolute = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };
  const [activeTab, setActiveTab] = useState(0);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dischargeForm, setDischargeForm] = useState({
    discharge_diagnosis: "",
    discharge_notes: "",
    discharge_plan: {
      discharge_condition: "Stable",
      discharge_destination: "Home",
      follow_up_required: true,
      follow_up_date: "",
      follow_up_doctor: "",
      follow_up_notes: "",
      discharge_instructions: "",
    },
  });
  const [transferForm, setTransferForm] = useState({
    new_bed_id: "",
    transfer_reason: "",
    notes: "",
  });
  const [editForm, setEditForm] = useState({
    admitting_doctor: "",
    department: "",
    admission_notes: "",
  });

  const queryClient = useQueryClient();

  // Fetch admission details
  const {
    data: admission,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["ipd-admission", id],
    queryFn: () => getIPDAdmission(id, token),
    enabled: !!id,
  });

  // Initialize edit form when admission loads or edit dialog opens
  useEffect(() => {
    if (admission && editOpen) {
      setEditForm({
        admitting_doctor: admission.admitting_doctor || "",
        department: admission.department || "",
        admission_notes: admission.admission_notes || "",
      });
    }
  }, [admission, editOpen]);

  // Loads for transfer dialog
  const { data: availableBeds } = useQuery({
    queryKey: ["ipd-available-beds"],
    queryFn: () => getAvailableBeds({}, token),
    enabled: transferOpen,
  });

  // Fetch doctors and departments for dialogs
  const { data: doctors } = useFetchObjects(
    ["users"],
    "users/?page_size=1000",
    token
  );

  const { data: departments } = useFetchObjects(
    ["departments"],
    "departments/?page_size=1000",
    token
  );

  const filteredDoctors = Array.isArray(doctors)
    ? doctors
    : doctors?.results || [];

  const { data: transfersData } = useQuery({
    queryKey: ["ipd-transfers", id],
    queryFn: () => listIPDTransfers({ admission: id }, token),
    enabled: !!id,
  });

  const { data: auditsData } = useQuery({
    queryKey: ["ipd-admission-audits", id],
    queryFn: () => listAdmissionAudits(id, token),
    enabled: !!id,
  });

  // Ward rounds history for this admission
  const { data: roundsData } = useQuery({
    queryKey: ["ward-rounds-admission", id],
    queryFn: () => listWardRounds({ admission: id, page_size: 20 }, token),
    enabled: !!id,
  });

  // Investigations scoped to this admission
  const { data: labOrders } = useQuery({
    queryKey: ["lab-orders", id],
    queryFn: () => listLabOrders({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });
  const { data: xrays } = useQuery({
    queryKey: ["patient-xrays", id],
    queryFn: () => listPatientXrays({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });
  const { data: ultrasounds } = useQuery({
    queryKey: ["ultrasounds", id],
    queryFn: () => listUltrasounds({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });

  const [resultOpen, setResultOpen] = useState(false);
  const [resultPayload, setResultPayload] = useState(null); // { type: 'xray'|'ultrasound'|'lab', data }
  const tempChartRef = useRef(null);
  const hrChartRef = useRef(null);
  const spo2ChartRef = useRef(null);
  const [printTicketOpen, setPrintTicketOpen] = useState(false);
  const [ticketToPrint, setTicketToPrint] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);

  const roundsItems = useMemo(() => {
    const items = Array.isArray(roundsData)
      ? roundsData
      : roundsData?.results || [];
    return items
      .slice()
      .sort((a, b) => new Date(a.round_date) - new Date(b.round_date));
  }, [roundsData]);

  const buildSeries = (key) => {
    const labels = roundsItems.map((r) =>
      new Date(r.round_date).toLocaleString()
    );
    const values = roundsItems.map((r) => r[key] ?? null);
    return {
      labels,
      datasets: [
        {
          label: key,
          data: values,
          borderColor: "#1976d2",
          backgroundColor: "rgba(25,118,210,0.15)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    };
  };

  const downloadChart = (ref, filename) => {
    try {
      const chart = ref.current;
      if (chart) {
        const url = chart.toBase64Image();
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
      }
    } catch (e) {}
  };

  // Mutations
  const dischargeMutation = useMutation({
    mutationFn: (payload) => dischargePatient(id, payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["ipd-admission", id]);
      queryClient.invalidateQueries(["ipd-available-beds"]);
      setDischargeOpen(false);
    },
  });

  const transferMutation = useMutation({
    mutationFn: (payload) => transferPatient(id, payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["ipd-admission", id]);
      queryClient.invalidateQueries(["ipd-available-beds"]);
      queryClient.invalidateQueries(["ipd-transfers", id]);
      setTransferOpen(false);
      setTransferForm({ new_bed_id: "", transfer_reason: "", notes: "" });
    },
  });

  const editMutation = useMutation({
    mutationFn: (payload) => updateAdmissionDetails(id, payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["ipd-admission", id]);
      queryClient.invalidateQueries(["ipd-admission-audits", id]);
      setEditOpen(false);
    },
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success"; // Admitted
      case 2:
        return "info"; // Discharged
      case 3:
        return "warning"; // Transferred
      case 4:
        return "error"; // Absent
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("ipd.admissions.status.admitted");
      case 2:
        return t("ipd.admissions.status.discharged");
      case 3:
        return t("ipd.admissions.status.transferred");
      case 4:
        return t("ipd.admissions.status.absent");
      default:
        return t("ipd.admissions.status.unknown");
    }
  };

  const getAdmissionTypeText = (type) => {
    switch (type) {
      case 1:
        return t("ipd.admissions.type.emergency");
      case 2:
        return t("ipd.admissions.type.elective");
      case 3:
        return t("ipd.admissions.type.transfer");
      case 4:
        return t("ipd.admissions.type.observation");
      default:
        return t("ipd.admissions.type.unknown");
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("ipd.admissions.error_loading")} - {error?.message}
        </Alert>
      </Box>
    );
  }

  if (!admission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t("ipd.admissions.error_loading")}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/ipd/admissions")}
          >
            {t("common.back")}
          </Button>
          <Typography variant="h4" component="h1">
            {t("ipd.admissions.admission_details")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {admission.status === 1 && (
            <>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setTransferOpen(true)}
                sx={{ mr: 1 }}
              >
                {t("ipd.admissions.transfer_patient", "Transfer Patient")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setDischargeOpen(true)}
                sx={{ mr: 1 }}
              >
                {t("ipd.admissions.discharge_patient", "Discharge Patient")}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setEditOpen(true)}
          >
            {t("ipd.admissions.edit_details", "Edit Details")}
          </Button>
          <Tooltip title={t("common.print")}>
            <IconButton color="primary">
              <Print />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.share")}>
            <IconButton color="primary">
              <Share />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Status Banner */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "primary.light",
            color: "primary.contrastText",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {admission.patient_name}
              </Typography>
              <Typography variant="body2">
                {t("ipd.admissions.patient")} • {admission.patient_phone}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={getStatusText(admission.status)}
              color={getStatusColor(admission.status)}
              variant="filled"
              sx={{ color: "white" }}
            />
          </Box>
        </Paper>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left Column - Patient & Admission Info */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.patient_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Person color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.patient")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {admission.patient_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {admission.patient_phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Bed color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.bed")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {admission.bed_info}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {admission.ward_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <LocalHospital color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.admitting_doctor")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {admission.admitting_doctor_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <MedicalServices color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.department")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {admission.department_name}
                  </Typography>
                </Grid>
                {admission.ticket_number_display && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Assignment color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {t("ipd.admissions.ticket_number", "Ticket Number")}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.ticket_number_display}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Print />}
                        onClick={async () => {
                          if (admission.ticket) {
                            setLoadingTicket(true);
                            try {
                              const response = await axios.get(
                                `${process.env.REACT_APP_API_URL || ""}/tickets/${admission.ticket}/`,
                                {
                                  headers: { Authorization: `Token ${token}` },
                                }
                              );
                              setTicketToPrint(response.data);
                              setPrintTicketOpen(true);
                            } catch (err) {
                              console.error("Error fetching ticket:", err);
                            } finally {
                              setLoadingTicket(false);
                            }
                          }
                        }}
                        disabled={loadingTicket || !admission.ticket}
                      >
                        {t("ipd.admissions.print_ticket", "Print Ticket")}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Admission Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.admission_details")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CalendarToday color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.admission_date")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(admission.admission_date).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(admission.admission_date).toLocaleTimeString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Assignment color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.type_label")}
                    </Typography>
                  </Box>
                  <Chip
                    label={getAdmissionTypeText(admission.admission_type)}
                    color="info"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Description color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t("ipd.admissions.diagnosis")}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {admission.admission_diagnosis}
                  </Typography>
                </Grid>
                {admission.admission_notes && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Info color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {t("ipd.admissions.notes")}
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {admission.admission_notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Ward Rounds History */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.ward_rounds", "Ward Rounds")}
              </Typography>
              {/* Vitals Trends */}
              {roundsItems.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t("ipd.admissions.vitals_trends", "Vitals Trends")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="body2">
                            Temperature (°C)
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              downloadChart(
                                tempChartRef,
                                `temperature-${id}.png`
                              )
                            }
                          >
                            Download
                          </Button>
                        </Box>
                        <Line
                          ref={tempChartRef}
                          data={buildSeries("temperature")}
                          options={{
                            plugins: { legend: { display: false } },
                            scales: { x: { display: false } },
                          }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="body2">
                            Heart Rate (bpm)
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              downloadChart(hrChartRef, `heart-rate-${id}.png`)
                            }
                          >
                            Download
                          </Button>
                        </Box>
                        <Line
                          ref={hrChartRef}
                          data={buildSeries("heart_rate")}
                          options={{
                            plugins: { legend: { display: false } },
                            scales: { x: { display: false } },
                          }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="body2">SpO2 (%)</Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              downloadChart(spo2ChartRef, `spo2-${id}.png`)
                            }
                          >
                            Download
                          </Button>
                        </Box>
                        <Line
                          ref={spo2ChartRef}
                          data={buildSeries("oxygen_saturation")}
                          options={{
                            plugins: { legend: { display: false } },
                            scales: { x: { display: false } },
                          }}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {(() => {
                const items = Array.isArray(roundsData)
                  ? roundsData
                  : roundsData?.results || [];
                if (!items.length) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "ipd.admissions.no_rounds_recorded",
                        "No rounds recorded"
                      )}
                    </Typography>
                  );
                }
                return (
                  <Grid container spacing={2}>
                    {items.map((r) => (
                      <Grid item xs={12} md={6} key={r.id}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2">
                            {new Date(r.round_date).toLocaleString()} —{" "}
                            {r.doctor_name}
                          </Typography>
                          {r.subjective && (
                            <Typography variant="body2">
                              S: {r.subjective}
                            </Typography>
                          )}
                          {r.objective && (
                            <Typography variant="body2">
                              O: {r.objective}
                            </Typography>
                          )}
                          {r.assessment && (
                            <Typography variant="body2">
                              A: {r.assessment}
                            </Typography>
                          )}
                          {r.plan && (
                            <Typography variant="body2">P: {r.plan}</Typography>
                          )}
                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            {r.temperature && (
                              <Grid item>
                                <Typography variant="caption">
                                  Temp: {r.temperature}°C
                                </Typography>
                              </Grid>
                            )}
                            {r.blood_pressure_systolic &&
                              r.blood_pressure_diastolic && (
                                <Grid item>
                                  <Typography variant="caption">
                                    BP: {r.blood_pressure_systolic}/
                                    {r.blood_pressure_diastolic}
                                  </Typography>
                                </Grid>
                              )}
                            {r.heart_rate && (
                              <Grid item>
                                <Typography variant="caption">
                                  HR: {r.heart_rate} bpm
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
            </Paper>

            {/* Investigations */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.investigations", "Investigations")}
              </Typography>
              {(() => {
                const lab = Array.isArray(labOrders)
                  ? labOrders
                  : labOrders?.results || [];
                const xr = Array.isArray(xrays) ? xrays : xrays?.results || [];
                const us = Array.isArray(ultrasounds)
                  ? ultrasounds
                  : ultrasounds?.results || [];
                if (!lab.length && !xr.length && !us.length) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "ipd.admissions.no_investigations_found",
                        "No investigations found"
                      )}
                    </Typography>
                  );
                }
                return (
                  <List>
                    {/* Lab orders */}
                    {lab.map((o) => (
                      <ListItem
                        key={`lab-${o.id}`}
                        dense
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => {
                              setResultPayload({ type: "lab", data: o });
                              setResultOpen(true);
                            }}
                          >
                            View Result
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={`Lab Order #${o.id} — ${new Date(
                            o.order_date
                          ).toLocaleString()}`}
                          secondary={`Tests: ${(o.tests_display || [])
                            .map(
                              (t) =>
                                t.test_type_display?.name ||
                                t.test_type_display ||
                                t.test_type_name ||
                                ""
                            )
                            .filter(Boolean)
                            .join(", ")}`}
                        />
                      </ListItem>
                    ))}
                    {/* X-rays */}
                    {xr.map((x) => (
                      <ListItem
                        key={`xray-${x.id}`}
                        dense
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => {
                              setResultPayload({ type: "xray", data: x });
                              setResultOpen(true);
                            }}
                          >
                            View Result
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={`X-Ray — ${
                            x.xray_type?.name || x.xray_type_display || ""
                          }`}
                          secondary={`${new Date(
                            x.date
                          ).toLocaleDateString()} • ${x.status_display || ""}`}
                        />
                      </ListItem>
                    ))}
                    {/* Ultrasounds */}
                    {us.map((u) => (
                      <ListItem
                        key={`us-${u.id}`}
                        dense
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => {
                              setResultPayload({ type: "ultrasound", data: u });
                              setResultOpen(true);
                            }}
                          >
                            View Result
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={`Ultrasound — ${
                            u.ultrasound_type?.name ||
                            u.ultrasound_type_display ||
                            ""
                          }`}
                          secondary={`${new Date(
                            u.date
                          ).toLocaleDateString()} • ${u.status_display || ""}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                );
              })()}
            </Paper>

            {/* Nurse Assignments */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <NurseAssignments admissionId={admission.id} />
              
              {/* Prescriptions Section */}
              {admission && admission.patient && (
                <PrescriptionsList
                  patient={{ id: admission.patient }}
                  doctor={{ id: admission.admitting_doctor }}
                />
              )}
            </Paper>

            {/* Medical Equipment & Processes */}
            <MedicalEquipmentManager
              admissionId={admission.id}
              patientName={admission.patient_name}
            />
          </Grid>

          {/* Right Column - Timeline & Financial */}
          <Grid item xs={12} md={4}>
            {/* Actions Quick Panel */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("ipd.admissions.actions", "Actions")}
              </Typography>
              {admission.status === 1 && (
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setTransferOpen(true)}
                  >
                    {t("ipd.admissions.transfer", "Transfer")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => setDischargeOpen(true)}
                  >
                    {t("ipd.admissions.discharge", "Discharge")}
                  </Button>
                </Box>
              )}
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditOpen(true)}
                  startIcon={<Edit />}
                >
                  {t("ipd.admissions.edit_details", "Edit Details")}
                </Button>
              </Box>
            </Paper>

            {/* Financial Summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.financial_summary", "Financial Summary")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <AttachMoney color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ipd.admissions.total_charges", "Total Charges")}
                    secondary={`${admission.total_charges} AFN`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ipd.admissions.total_paid", "Total Paid")}
                    secondary={`${admission.total_paid} AFN`}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <Warning color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Balance"
                    secondary={`${admission.balance} AFN`}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Transfers History */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.transfers", "Transfers")}
              </Typography>
              {transfersData &&
              transfersData.results &&
              transfersData.results.length > 0 ? (
                <List>
                  {transfersData.results.map((tr) => (
                    <ListItem key={tr.id} dense>
                      <ListItemText
                        primary={`${tr.from_bed_info} → ${tr.to_bed_info}`}
                        secondary={`${new Date(
                          tr.transfer_date
                        ).toLocaleString()} — ${tr.transfer_reason || ""}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("ipd.admissions.no_transfers", "No transfers")}
                </Typography>
              )}
            </Paper>

            {/* Audit Trail */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Audit Trail
              </Typography>
              {auditsData &&
              auditsData.results &&
              auditsData.results.length > 0 ? (
                <List>
                  {auditsData.results.map((a) => (
                    <ListItem key={a.id} dense>
                      <ListItemText
                        primary={`${a.field_name}: ${a.old_value || "-"} → ${
                          a.new_value || "-"
                        }`}
                        secondary={`${new Date(
                          a.changed_at
                        ).toLocaleString()} — ${a.changed_by_name || ""}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No changes
                </Typography>
              )}
            </Paper>

            {/* Timeline */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.timeline", "Timeline")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Timeline color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("ipd.admissions.admitted", "Admitted")}
                    secondary={new Date(
                      admission.admission_date
                    ).toLocaleString()}
                  />
                </ListItem>
                {admission.expected_discharge_date && (
                  <ListItem>
                    <ListItemIcon>
                      <CalendarToday color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(
                        "ipd.admissions.expected_discharge",
                        "Expected Discharge"
                      )}
                      secondary={new Date(
                        admission.expected_discharge_date
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                {admission.actual_discharge_date && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("ipd.admissions.discharged", "Discharged")}
                      secondary={new Date(
                        admission.actual_discharge_date
                      ).toLocaleString()}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      {/* Result Dialog */}
      <Dialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("ipd.admissions.investigation_result", "Investigation Result")}
        </DialogTitle>
        <DialogContent dividers>
          {resultPayload?.type === "xray" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {resultPayload.data?.xray_type?.name ||
                  resultPayload.data?.xray_type_display}
              </Typography>
              {resultPayload.data?.result_data ? (
                <Box>
                  {resultPayload.data.result_data?.image && (
                    <Box mb={2}>
                      <a
                        href={toAbsolute(resultPayload.data.result_data.image)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={toAbsolute(resultPayload.data.result_data.image)}
                          alt="X-Ray"
                          style={{
                            maxWidth: "100%",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    </Box>
                  )}
                  {resultPayload.data.result_data?.findings && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        Findings:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {resultPayload.data.result_data.findings}
                        </Typography>
                      </Paper>
                    </>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("ipd.admissions.no_result_yet", "No result yet.")}
                </Alert>
              )}
            </>
          )}
          {resultPayload?.type === "ultrasound" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {resultPayload.data?.ultrasound_type?.name ||
                  resultPayload.data?.ultrasound_type_display}
              </Typography>
              {resultPayload.data?.result_data ? (
                <Box>
                  {resultPayload.data.result_data?.image && (
                    <Box mb={2}>
                      <a
                        href={toAbsolute(resultPayload.data.result_data.image)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={toAbsolute(resultPayload.data.result_data.image)}
                          alt="Ultrasound"
                          style={{
                            maxWidth: "100%",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    </Box>
                  )}
                  {resultPayload.data.result_data?.findings && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2">
                        {resultPayload.data.result_data.findings}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("ipd.admissions.no_result_yet", "No result yet.")}
                </Alert>
              )}
            </>
          )}
          {resultPayload?.type === "lab" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Lab Order #{resultPayload.data?.id}
              </Typography>
              {(resultPayload.data?.tests_display || []).map((t) => {
                const d = t.test_type_display || {};
                const hasRange =
                  d.reference_range ||
                  (d.min_value !== null &&
                    d.min_value !== undefined &&
                    d.max_value !== null &&
                    d.max_value !== undefined);
                return (
                  <Paper key={t.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {d.name}
                    </Typography>
                    {hasRange && (
                      <Typography variant="caption" color="text.secondary">
                        Normal range:{" "}
                        {d.reference_range
                          ? d.reference_range
                          : `${d.min_value}–${d.max_value}${
                              d.units ? ` ${d.units}` : ""
                            }`}
                      </Typography>
                    )}
                    {d.result_value ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Result: {d.result_value}
                        {d.units ? ` ${d.units}` : ""}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("ipd.admissions.no_result_yet", "No result yet")}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>
            {t("common.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Discharge Dialog */}
      <Dialog
        open={dischargeOpen}
        onClose={() => setDischargeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("ipd.admissions.discharge_patient", "Discharge Patient")}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t(
              "ipd.admissions.discharge_diagnosis",
              "Discharge Diagnosis"
            )}
            value={dischargeForm.discharge_diagnosis}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_diagnosis: e.target.value,
              })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("ipd.admissions.discharge_notes", "Discharge Notes")}
            multiline
            minRows={2}
            value={dischargeForm.discharge_notes}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_notes: e.target.value,
              })
            }
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Discharge Plan
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label="Instructions"
            value={dischargeForm.discharge_plan.discharge_instructions}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_plan: {
                  ...dischargeForm.discharge_plan,
                  discharge_instructions: e.target.value,
                },
              })
            }
          />
          <TextField
            fullWidth
            type="date"
            margin="dense"
            label="Follow-up Date"
            InputLabelProps={{ shrink: true }}
            value={dischargeForm.discharge_plan.follow_up_date}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_plan: {
                  ...dischargeForm.discharge_plan,
                  follow_up_date: e.target.value,
                },
              })
            }
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Follow-up Doctor</InputLabel>
            <Select
              value={dischargeForm.discharge_plan.follow_up_doctor}
              label="Follow-up Doctor"
              onChange={(e) =>
                setDischargeForm({
                  ...dischargeForm,
                  discharge_plan: {
                    ...dischargeForm.discharge_plan,
                    follow_up_doctor: e.target.value,
                  },
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              {filteredDoctors.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.first_name} {doctor.last_name}
                  {doctor.username && ` (${doctor.username})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => dischargeMutation.mutate(dischargeForm)}
            variant="contained"
            disabled={dischargeMutation.isPending}
          >
            {t("ipd.admissions.discharge", "Discharge")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("ipd.admissions.transfer_patient", "Transfer Patient")}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label={t("ipd.admissions.new_bed", "New Bed")}
            value={transferForm.new_bed_id}
            onChange={(e) =>
              setTransferForm({ ...transferForm, new_bed_id: e.target.value })
            }
          >
            {(availableBeds || []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.room_number} - Bed {b.bed_number} ({b.ward_name})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label="Reason"
            value={transferForm.transfer_reason}
            onChange={(e) =>
              setTransferForm({
                ...transferForm,
                transfer_reason: e.target.value,
              })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Notes"
            value={transferForm.notes}
            onChange={(e) =>
              setTransferForm({ ...transferForm, notes: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => transferMutation.mutate(transferForm)}
            variant="contained"
            disabled={transferMutation.isPending || !transferForm.new_bed_id}
          >
            {t("ipd.admissions.transfer", "Transfer")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Details Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("ipd.admissions.edit_admission_details", "Edit Admission Details")}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Admitting Doctor</InputLabel>
            <Select
              value={editForm.admitting_doctor}
              label={t("ipd.admissions.admitting_doctor", "Admitting Doctor")}
              onChange={(e) =>
                setEditForm({ ...editForm, admitting_doctor: e.target.value })
              }
            >
              {filteredDoctors.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.first_name} {doctor.last_name}
                  {doctor.username && ` (${doctor.username})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Department</InputLabel>
            <Select
              value={editForm.department}
              label="Department"
              onChange={(e) =>
                setEditForm({ ...editForm, department: e.target.value })
              }
            >
              {(Array.isArray(departments)
                ? departments
                : departments?.results || []
              ).map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            label="Notes"
            multiline
            minRows={2}
            value={editForm.admission_notes}
            onChange={(e) =>
              setEditForm({ ...editForm, admission_notes: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => editMutation.mutate(editForm)}
            variant="contained"
            disabled={editMutation.isPending}
          >
            {t("common.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Print Dialog */}
      {ticketToPrint && (
        <Dialog
          open={printTicketOpen}
          onClose={() => {
            setPrintTicketOpen(false);
            setTicketToPrint(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            <TicketPrint
              ticket={ticketToPrint}
              onClose={() => {
                setPrintTicketOpen(false);
                setTicketToPrint(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AdmissionView;
